import { authenticatedFetch } from '../../../shared/utils/authenticatedFetch';
// A views/ import from a service, deliberately: boardCache is a plain localStorage utility (no
// React, no components), and this is the FLOOR under "every link mutation drops the cached
// boards" — callers invalidate their own react-query state, but forgetting this one cost a
// stale-board bug (Aug 2026), so it now cannot be forgotten.
import { clearAllBoardCaches } from '../views/taActionBoard/boardCache';
import type { LinkShareFields } from './linkedinShareApi';

/** A "/" menu variable an author can insert; resolves per candidate at send time. */
export interface TokenDef { name: string; label: string; desc: string }
/** One language of a per-link invite email. */
export interface InviteEmailLang { subject: string; body: string }
/** A link's OWN invite wording — stored only when deliberately customised (`custom: true`). */
export interface InviteEmail { custom?: boolean; templateKey?: string | null; blockKeys: string[]; de: InviteEmailLang; en: InviteEmailLang; customisedBy?: string | null; customisedAt?: string | null }

const PROXY_URL =
  import.meta.env.VITE_PROXY_URL ||
  'https://proxy-prod-people-520260363689.europe-west3.run.app';

const BASE = `${PROXY_URL}/api/interview-links`;

/** Option sets mirror the proxy's validation — keep both in step. */
export const LENGTH_OPTIONS = [15, 20, 30, 45, 60, 75, 90, 120];
/** Start-time granularity: 15 lets candidates book 09:15/09:45, 30 only on the half hour, 60 on the hour. */
export const INTERVAL_OPTIONS = [15, 30, 60];
export const BUFFER_OPTIONS = [0, 5, 10, 15, 30];
export const EXPIRY_OPTIONS = [7, 14, 30, 60, 90];
export const RANGE_OPTIONS = [7, 14, 21, 30];
export const NOTICE_OPTIONS = [0, 2, 4, 6, 12, 24, 48];
/** Earliest calendar day a candidate may book, counted from the day they open the link. */
export const EARLIEST_DAY_OPTIONS = [0, 1, 2, 3, 5, 7, 14];

export const WEEKDAYS = ['mon', 'tue', 'wed', 'thu', 'fri', 'sat', 'sun'] as const;
export type Weekday = (typeof WEEKDAYS)[number];

export const WEEKDAY_LABELS: Record<Weekday, string> = {
  mon: 'Monday',
  tue: 'Tuesday',
  wed: 'Wednesday',
  thu: 'Thursday',
  fri: 'Friday',
  sat: 'Saturday',
  sun: 'Sunday',
};

export type MeetingType = '1:1' | 'panel';

export interface TimeWindow {
  start: string;
  end: string;
}

export interface LinkInterviewer {
  email: string;
  name?: string | null;
  timezone: string;
  availability: Record<Weekday, TimeWindow[]>;
  /** Max live interviews per day for this person, counted across all links (enforced server-side in panelBusy). */
  dailyLimit?: number | null;
}

/**
 * The stage's post-rejection feedback call: who takes the 15-minute call a
 * rejected candidate can book, and when they are bookable. The length is fixed
 * at 15 minutes by the rejection email's own wording, so it is not stored.
 */
export interface FeedbackCallConfig {
  enabled: boolean;
  interviewers: LinkInterviewer[];
}

/**
 * Stages whose REJECTION email offers the 15-min feedback call — the case and
 * final buckets. Mirrors proxy-server/lib/rejectionDraft.js STAGE_BUCKET: a
 * first-interview rejection invites an email reply instead, which is why a
 * first-stage link defaults to no feedback call.
 */
const FEEDBACK_CALL_STAGE_IDS = new Set([
  '27f97232-81dc-402a-aa54-aaf18607f3e0', // Case / Technical Interview
  '224c16e6-7210-4344-8a81-e51dba633b90', // Final & Bar Raiser Interview
]);

export function stageOffersFeedbackCall(stageId: string): boolean {
  return FEEDBACK_CALL_STAGE_IDS.has(stageId);
}

export interface InterviewLink extends LinkShareFields {
  id: string;
  /** 'screening' = a TA manager's own screening link (Oct 24 2026); absent = role + stage link. */
  kind?: 'screening' | null;
  name: string;
  meetingType: MeetingType;
  jobTitle: string;
  /** Lever stage id — the join key with a Lever candidate. */
  stageId: string;
  /** Canonical Lever stage name, shown to the candidate. */
  stageName: string;
  /** Lever posting id — required; how the automation finds this link. */
  postingId: string;
  /**
   * Lever feedback template attached to every interview booked through this link.
   * Null means the interview arrives in Lever with no form for the interviewer
   * to fill in — which is what links created before this field existed do.
   */
  feedbackTemplate?: string | null;
  /** TA manager who owns this role's process — the list's primary filter. */
  taOwner?: string | null;
  /** Own invite wording, only when customised. Null = follow the Email Library live. */
  inviteEmail?: InviteEmail | null;
  /**
   * The 15-min post-rejection feedback call for this stage. Null = never
   * configured (every link created before this field existed) — the rejection
   * email then falls back to the recruiter overview sheet's booking page.
   */
  feedbackCall?: FeedbackCallConfig | null;
  minutes: number;
  interval: number;
  bufferBefore: number;
  bufferAfter: number;
  interviewers: LinkInterviewer[];
  /** Days each CANDIDATE has to book, from their own invite. Not the link's lifetime. */
  expiryDays: number;
  offeredRangeDays: number;
  minNoticeHours: number;
  /** 0 = today. Combined with minNoticeHours by taking whichever lands later. */
  earliestBookableDays?: number;
  sendConfirmationEmail: boolean;
  /** Offers "Suggest a time" on the booking page; nothing is booked. Absent = off. */
  allowTimeSuggestions?: boolean;
  /**
   * Which language this role's AUTOMATIC booking emails go out in — the confirmation, the
   * candidate's own reschedule and the cancellation, which nobody presses send on and which
   * therefore have nowhere else to learn a language from. Defaults to English, which is what
   * they all said before the field existed.
   */
  language?: 'de' | 'en';
  active: boolean;
  /** Server-computed: its Lever posting is closed, so it takes no new bookings. */
  roleClosed?: boolean;
  createdAt: string;
  updatedAt: string;
  createdBy?: string | null;
}

/** What the form submits — no id/token/timestamps, the proxy owns those. */
export type InterviewLinkDraft = Omit<InterviewLink, 'id' | 'createdAt' | 'updatedAt' | 'createdBy' | 'roleClosed'>;

export class InterviewLinkError extends Error {
  status: number;
  constructor(message: string, status: number) {
    super(message);
    this.name = 'InterviewLinkError';
    this.status = status;
  }
}

/** A candidate whose booking window closed without a booking — resend fodder. */
export interface ExpiredInvite {
  token: string;
  linkId: string;
  candidateName: string;
  candidateEmail: string;
  opportunityId: string | null;
  roleTitle: string;
  stageName: string;
  postingId: string | null;
  language?: 'de' | 'en'; // the link's own; the resend dialog opens in it, absent ⇒ German
  invitedAt: string | null;
  expiredAt: string | null;
  linkActive: boolean;
  leverUrl: string | null;
}

/** The pre-drafted follow-up returned when a new link is minted. */
export interface ResendResult {
  invite: { token: string; bookingUrl: string; candidateName: string; candidateEmail: string };
  email: { de: InviteEmailLangText; en: InviteEmailLangText };
}
interface InviteEmailLangText {
  subject: string;
  body: string;
}

/**
 * Candidates whose interview link expired without a booking and who have not yet
 * had their one second chance. Powers the SLA tab's "send a new link" nudge.
 */
export async function fetchExpiredUnbooked(owner?: string): Promise<ExpiredInvite[]> {
  const qs = owner ? `?owner=${encodeURIComponent(owner)}` : '';
  const res = await authenticatedFetch(`${BASE}/expired-unbooked${qs}`);
  if (!res.ok) await readError(res, 'Could not load expired links');
  const data = (await res.json()) as { items: ExpiredInvite[] };
  return data.items;
}

/**
 * Mint the candidate's ONE second-chance link and get the pre-drafted follow-up
 * email back. The proxy refuses a second attempt (409) — the chance is spent the
 * moment the new link is generated.
 */
export async function resendExpiredInvite(linkId: string, token: string): Promise<ResendResult> {
  const res = await authenticatedFetch(
    `${BASE}/${encodeURIComponent(linkId)}/invites/${encodeURIComponent(token)}/resend`,
    { method: 'POST' }
  );
  if (!res.ok) await readError(res, 'Could not send a new link');
  return (await res.json()) as ResendResult;
}

async function readError(res: Response, fallback: string): Promise<never> {
  let message = fallback;
  try {
    const body = (await res.json()) as { error?: string };
    if (body?.error) message = body.error;
  } catch {
    // Non-JSON error body — keep the fallback.
  }
  throw new InterviewLinkError(message, res.status);
}

export function emptyAvailability(): Record<Weekday, TimeWindow[]> {
  return {
    mon: [{ start: '09:00', end: '17:00' }],
    tue: [{ start: '09:00', end: '17:00' }],
    wed: [{ start: '09:00', end: '17:00' }],
    thu: [{ start: '09:00', end: '17:00' }],
    fri: [{ start: '09:00', end: '17:00' }],
    sat: [],
    sun: [],
  };
}

/** Lever's defaults, so a new link starts where a TA manager expects. */
export function emptyDraft(): InterviewLinkDraft {
  return {
    name: '',
    meetingType: '1:1',
    jobTitle: '',
    stageId: '',
    stageName: '',
    postingId: '',
    feedbackTemplate: '',
    taOwner: '',
    inviteEmail: null,
    feedbackCall: null,
    minutes: 30,
    interval: 15,
    bufferBefore: 0,
    bufferAfter: 0,
    interviewers: [],
    expiryDays: 7,
    offeredRangeDays: 14,
    minNoticeHours: 6,
    earliestBookableDays: 0,
    sendConfirmationEmail: true,
    allowTimeSuggestions: false,
    language: 'en',
    active: true,
  };
}

/**
 * List links, optionally narrowed.
 *
 * `postingId` + `stage` is how an automation finds the right link for a Lever
 * candidate — Lever already knows both, so nothing has to be tagged by hand.
 */
export async function fetchInterviewLinks(
  filter: { postingId?: string; stage?: string; activeOnly?: boolean } = {}
): Promise<InterviewLink[]> {
  const params = new URLSearchParams();
  if (filter.postingId) params.set('postingId', filter.postingId);
  if (filter.stage) params.set('stage', filter.stage);
  if (filter.activeOnly) params.set('active', 'true');
  const qs = params.toString();

  const res = await authenticatedFetch(qs ? `${BASE}?${qs}` : BASE);
  if (!res.ok) await readError(res, 'Could not load interview links');
  const data = (await res.json()) as { links: InterviewLink[] };
  return data.links;
}

export async function createInterviewLink(draft: InterviewLinkDraft): Promise<InterviewLink> {
  const res = await authenticatedFetch(BASE, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(draft),
  });
  if (!res.ok) await readError(res, 'Could not create the interview link');
  clearAllBoardCaches(); // the board bakes `linkFound` into a cache that never refetches
  const data = (await res.json()) as { link: InterviewLink };
  return data.link;
}

export async function updateInterviewLink(
  id: string,
  draft: InterviewLinkDraft
): Promise<InterviewLink> {
  const res = await authenticatedFetch(`${BASE}/${encodeURIComponent(id)}`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(draft),
  });
  if (!res.ok) await readError(res, 'Could not save the interview link');
  clearAllBoardCaches(); // the board bakes `linkFound` into a cache that never refetches
  const data = (await res.json()) as { link: InterviewLink };
  return data.link;
}

/** Soft delete — links already sent to candidates must not start 404ing. */
export async function deactivateInterviewLink(id: string): Promise<void> {
  const res = await authenticatedFetch(`${BASE}/${encodeURIComponent(id)}`, { method: 'DELETE' });
  if (!res.ok) await readError(res, 'Could not deactivate the interview link');
  clearAllBoardCaches(); // the board bakes `linkFound` into a cache that never refetches
}

export interface CandidateInvite {
  token: string;
  bookingUrl: string;
  linkId: string;
  candidateName: string;
  candidateEmail: string;
  /** Null when no Lever candidate was given — the booking then skips Lever. */
  opportunityId: string | null;
  createdAt: string;
  /** Set once this candidate has booked; null while still outstanding. */
  booking?: { start: string; end: string; meetLink: string | null } | null;
  /** Set when they booked and then cancelled — the link is bookable again. */
  cancelledAt?: string | null;
  /** Why the candidate cancelled, in their words. */
  cancelReason?: string | null;
  /** Why the candidate moved the interview, in their words. */
  rescheduleReason?: string | null;
}

/**
 * Mint a per-candidate booking link.
 *
 * Not called from the Interview Links tab — candidate links will be issued from
 * a separate tab. Kept because the per-candidate token is what stops two
 * candidates for the same role colliding on one booking record, and it is what
 * carries the Lever opportunity id that the Lever write-back needs.
 *
 * Each candidate needs their own token: bookings are recorded per token, so a
 * shared link would tell the second candidate it had "already been used". The
 * Lever opportunity id also rides on the invite — it is what lets the booking
 * write an interview back into Lever.
 */
export async function createInvite(
  linkId: string,
  payload: { candidateName: string; candidateEmail: string; opportunityId?: string }
): Promise<CandidateInvite> {
  const res = await authenticatedFetch(`${BASE}/${encodeURIComponent(linkId)}/invites`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  });
  if (!res.ok) await readError(res, 'Could not create the candidate link');
  const data = (await res.json()) as { invite: CandidateInvite };
  return data.invite;
}

export async function fetchInvites(linkId: string): Promise<CandidateInvite[]> {
  const res = await authenticatedFetch(`${BASE}/${encodeURIComponent(linkId)}/invites`);
  if (!res.ok) await readError(res, 'Could not load candidate links');
  const data = (await res.json()) as { invites: CandidateInvite[] };
  return data.invites;
}

export interface BookabilityPreview {
  /** ISO start of the earliest slot the rules permit, or null if none. */
  bookableFrom: string | null;
  slotsInWindow: number;
}

/**
 * When could a candidate opening this link right now actually book?
 *
 * Answered by the proxy using the real slot engine, for a draft that has not been
 * saved yet. Reimplementing the arithmetic here would drift from the booking page
 * the moment either side changed, and a preview that quietly disagrees with what
 * candidates see is worse than showing nothing.
 *
 * Ignores calendars on purpose: it reports what the RULES allow, so the real page
 * may offer something later once busy time is taken into account.
 */
export async function previewBookability(
  draft: InterviewLinkDraft
): Promise<BookabilityPreview | null> {
  try {
    const res = await authenticatedFetch(`${BASE}/preview`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(draft),
      // A draft mid-edit is routinely incomplete and answers 400. Without this,
      // authenticatedFetch raises a global API error before returning — so every
      // debounced keystroke on a half-filled form would surface one, and the
      // `return null` below could never run.
      suppressErrorStatuses: [400],
    });
    if (!res.ok) return null;
    return (await res.json()) as BookabilityPreview;
  } catch {
    return null;
  }
}

/** One interviewer's contribution to a link's remaining bookability. */
export interface AtRiskInterviewer {
  email: string;
  name: string;
  /** False when their calendar could not be read — missing knowledge, not "busy". */
  readable: boolean;
  /** Slots in the window this person could still take. 0 = they are the bottleneck. */
  slots: number;
}

/**
 * What the server's bookability assessment says about one link — the SINGLE
 * declaration of `assessLinkBookability`'s return shape. Both consumers below
 * derive from it, so a new field cannot be added to one and forgotten in the
 * other.
 */
export interface LinkAssessment {
  /**
   * 'closed' = switched off or its Lever role closed, unbookable whatever the calendars say;
   * 'unknown' = calendars unreadable, so no judgement (never "no slots").
   */
  risk: 'closed' | 'empty' | 'low' | 'unknown' | 'ok';
  slotsInWindow: number;
  firstBookable: string | null;
  offeredRangeDays: number;
  perInterviewer: AtRiskInterviewer[];
  /** Why no judgement was possible, when there is a reason. */
  slotError: string | null;
}

/**
 * A link the at-risk sweep is warning about. The risk narrowing is deliberate:
 * the server only reports links that are actually at risk, having filtered out
 * ok / unknown / closed before the payload is built.
 */
export type AtRiskLink = Omit<LinkAssessment, 'risk'> & {
  risk: 'empty' | 'low';
  linkId: string;
  name: string;
  jobTitle: string;
  stageName: string;
  taOwner: string | null;
  /** Candidates holding a live, unbooked invite to this link right now. */
  pendingInvites: number;
};

export interface AtRiskReport {
  items: AtRiskLink[];
  /** Links that were judged (active, with pending invites). */
  checkedLinks: number;
  /** Links skipped because their calendars could not be read. */
  unreadableLinks: number;
  generatedAt: string;
}

/**
 * Active links whose waiting candidates see a (nearly) empty booking page.
 *
 * Judged server-side with the same slot engine and calendar reads the public
 * booking page uses, so this report and the candidate's page cannot disagree.
 * The proxy caches the answer for a few minutes — refetching freely is fine.
 */
export async function fetchAtRiskLinks(): Promise<AtRiskReport> {
  const res = await authenticatedFetch(`${BASE}/at-risk`);
  if (!res.ok) await readError(res, 'Could not check link bookability');
  return (await res.json()) as AtRiskReport;
}

/** Live bookability of one link — the full assessment, unnarrowed. */
export type LinkBookability = LinkAssessment;

/**
 * Check ONE link's live bookability — used when a recruiter is about to send a
 * candidate link, so nobody mails a link whose booking page is already empty.
 * Null on any failure: the check is advisory and must never block the dialog.
 *
 * `suppressErrorStatuses` matters here (same reason as previewBookability):
 * without it `authenticatedFetch` funnels every non-ok status into the global
 * error reporter and throws, so a deleted link or a calendar outage would raise
 * a user-visible error banner from a purely advisory check.
 */
export async function fetchLinkBookability(id: string): Promise<LinkBookability | null> {
  try {
    const res = await authenticatedFetch(`${BASE}/${encodeURIComponent(id)}/bookability`, {
      suppressErrorStatuses: [400, 404, 502],
    });
    if (!res.ok) return null;
    const data = (await res.json()) as { bookability: LinkBookability };
    return data.bookability;
  } catch {
    return null;
  }
}
